import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { BaseProps } from '../common/props';
import { useEffect } from 'react';

export interface ISpinnerProps extends BaseProps {
  /**
   * Velikost od 10 výše, výchozí 50.
   */
  size?: number;
}

export type SpinnerProps = ISpinnerProps;

// unitless total length of SVG path, to which stroke-dash* properties are relative.
// https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/pathLength
// this value is the result of `<path d={SPINNER_TRACK} />.getTotalLength()` and works in all browsers:
const PATH_LENGTH = 280;
const MIN_STROKE_WIDTH = 16;
const STROKE_WIDTH = 4;
export enum SpinnerSize {
  SMALL = 20,
  STANDARD = 50,
  LARGE = 100,
}

/**
 * Clamps the given number between min and max values. Returns value if within
 * range, or closest bound.
 */
export function clamp(val: number, min: number, max: number) {
  if (val == null) {
    return val;
  }
  if (max < min) {
    throw new Error(`clamp: max cannot be less than min`);
  }
  return Math.min(Math.max(val, min), max);
}

/**
 * Komponenta pro zobrazení čekání, např. načtení dat apod.
 */
export const Spinner: React.FC<SpinnerProps> = ({ className, size }) => {
  let cls = classNames(Classes.SPINNER, className);
  let useSize = size ? (size < 10 ? 10 : size) : 50;

  // keep spinner track width consistent at all sizes (down to about 10px).
  const strokeWidth = Math.min(
    MIN_STROKE_WIDTH,
    (STROKE_WIDTH * SpinnerSize.LARGE) / useSize
  );

  const [val, setVal] = React.useState(0.25);

  useEffect(() => {
    let asc = true;
    let v = 0.25;
    const timeoutId = setInterval(() => {
      if (asc) {
        if (v >= 0.98) {
          asc = false;
        } else {
          v += 0.01;
        }
      } else {
        if (v <= 0.04) {
          asc = true;
        } else {
          v -= 0.01;
        }
      }
      setVal(v);
    }, 48);
    return () => clearTimeout(timeoutId);
  }, []);

  const value = val;
  const strokeOffset =
    PATH_LENGTH - PATH_LENGTH * (value == null ? 0.25 : clamp(value, 0, 1));

  return (
    <div className={cls}>
      <svg
        width={useSize}
        height={useSize}
        strokeWidth={strokeWidth}
        viewBox="1.00 1.00 98.00 98.00"
      >
        <path
          className={Classes.SPINNER_TRACK}
          d="M 50,50 m 0,-45 a 45,45 0 1 1 0,90 a 45,45 0 1 1 0,-90"
        ></path>
        <path
          className={Classes.SPINNER_HEAD}
          d="M 50,50 m 0,-45 a 45,45 0 1 1 0,90 a 45,45 0 1 1 0,-90"
          pathLength={PATH_LENGTH}
          strokeDasharray={`${PATH_LENGTH} ${PATH_LENGTH}`}
          strokeDashoffset={strokeOffset}
        ></path>
      </svg>
    </div>
  );
};
